import React, { useMemo, useState } from 'react';
import { Box, CircularProgress, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { STORY_UI } from '../../solution-stories/storyUiTokens';
import { SEARCE_COLORS } from '../../../theme';
import type { BillingBudget } from '../../../types/billing';
import SegmentDonutChart, { type DonutSegmentData } from '../../charts/SegmentDonutChart';
import {
  applyReportFilters,
  BillingReportFilters,
  EMPTY_REPORT_FILTERS,
  type BillingReportFilterState,
} from './BillingReportFilters';
import { BillingReportKpiCards } from './BillingReportKpiCards';
import { BillingReportTrendChart } from './BillingReportTrendChart';
import { BillingReportSplitBarChart } from './BillingReportSplitBarChart';
import {
  BillingReportTopMovers,
  TOP_OVERSPEND_COLOR,
  TOP_SAVINGS_COLOR,
} from './BillingReportTopMovers';
import { ReportCard } from './ReportCard';
import {
  computeGroupedSplit,
  computeMonthlyTrend,
  computeReportTotals,
  computeTopMovers,
  formatUsd,
  type BillingSplitDimension,
} from '../../../utils/billingReportMetrics';

const CATEGORICAL_PALETTE = [
  STORY_UI.accent,
  SEARCE_COLORS.secondary,
  SEARCE_COLORS.successDark,
  SEARCE_COLORS.warningDark,
  SEARCE_COLORS.info_600,
  STORY_UI.sky,
  SEARCE_COLORS.secondary_600,
  SEARCE_COLORS.errorDark,
];

const DIMENSION_LABELS: Record<BillingSplitDimension, string> = {
  business_unit: 'Business Unit',
  sub_bu: 'Sub-BU',
  cloud_type: 'Cloud Type',
};

export interface SandboxBillingReportViewProps {
  rows: BillingBudget[];
  isLoading: boolean;
}

export function SandboxBillingReportView({ rows, isLoading }: SandboxBillingReportViewProps): React.ReactElement {
  const [filters, setFilters] = useState<BillingReportFilterState>(EMPTY_REPORT_FILTERS);
  const [splitDimension, setSplitDimension] = useState<BillingSplitDimension>('business_unit');

  const filteredRows = useMemo(() => applyReportFilters(rows, filters), [rows, filters]);

  const totals = useMemo(() => computeReportTotals(filteredRows), [filteredRows]);
  const trend = useMemo(() => computeMonthlyTrend(filteredRows), [filteredRows]);
  const split = useMemo(() => computeGroupedSplit(filteredRows, splitDimension), [filteredRows, splitDimension]);
  const spendByBu = useMemo(() => computeGroupedSplit(filteredRows, 'business_unit'), [filteredRows]);
  const { topSavings, topOverspend } = useMemo(() => computeTopMovers(filteredRows), [filteredRows]);

  const budgetVsSpendDonut: DonutSegmentData[] = [
    { name: 'Spent', value: Math.round(totals.totalSpendUsd), color: SEARCE_COLORS.secondary },
    {
      name: 'Remaining',
      value: Math.max(0, Math.round(totals.totalBudgetUsd - totals.totalSpendUsd)),
      color: STORY_UI.accentTint,
    },
  ];

  const spendByBuDonut: DonutSegmentData[] = spendByBu
    .filter((g) => g.spend > 0)
    .map((g, i) => ({ name: g.name, value: g.spend, color: CATEGORICAL_PALETTE[i % CATEGORICAL_PALETTE.length] }));

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pb: 2 }}>
      <BillingReportFilters rows={rows} value={filters} onChange={setFilters} />

      <BillingReportKpiCards totals={totals} />

      <ReportCard title="Monthly Spend vs Budget" subtitle="USD, normalized across all account currencies">
        <BillingReportTrendChart data={trend} />
      </ReportCard>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
        <ReportCard title="Budget Utilization" subtitle={`${formatUsd(totals.totalSpendUsd)} of ${formatUsd(totals.totalBudgetUsd)}`}>
          <SegmentDonutChart
            data={budgetVsSpendDonut}
            height={280}
            centerValue={Math.round(totals.utilizationPct)}
            centerLabel="% Utilized"
            tooltipUnitLabel="USD"
          />
        </ReportCard>
        <ReportCard title="Spend Distribution by Business Unit">
          <SegmentDonutChart
            data={spendByBuDonut}
            height={280}
            centerValue={Math.round(totals.totalSpendUsd)}
            centerLabel="Total Spend"
            tooltipUnitLabel="USD"
          />
        </ReportCard>
      </Box>

      <ReportCard
        title="Budget vs Spend by Dimension"
        action={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={splitDimension}
            onChange={(_e, v) => v && setSplitDimension(v)}
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontFamily: STORY_UI.fontSans,
                fontSize: '12px',
                fontWeight: 500,
                borderRadius: STORY_UI.radiusPill,
                border: `1px solid ${STORY_UI.line}`,
                color: STORY_UI.inkSoft,
                px: 1.5,
                py: 0.5,
                '&.Mui-selected': {
                  backgroundColor: STORY_UI.ink,
                  color: '#fff',
                  '&:hover': { backgroundColor: STORY_UI.ink },
                },
              },
            }}
          >
            {(Object.keys(DIMENSION_LABELS) as BillingSplitDimension[]).map((d) => (
              <ToggleButton key={d} value={d}>
                {DIMENSION_LABELS[d]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        }
      >
        <BillingReportSplitBarChart data={split} />
      </ReportCard>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
        <ReportCard title="Top Savings Accounts" subtitle="Completed months, budget − spend">
          <BillingReportTopMovers
            title="Highest savings"
            rows={topSavings}
            color={TOP_SAVINGS_COLOR}
            emptyMessage="No accounts under budget yet."
          />
        </ReportCard>
        <ReportCard title="Top Overspend Accounts" subtitle="Completed months, spend − budget">
          <BillingReportTopMovers
            title="Highest overspend"
            rows={topOverspend}
            color={TOP_OVERSPEND_COLOR}
            emptyMessage="No accounts over budget. 🎉"
          />
        </ReportCard>
      </Box>

      {filteredRows.length === 0 && (
        <Typography sx={{ textAlign: 'center', color: STORY_UI.inkFaint, fontFamily: STORY_UI.fontSans, py: 4 }}>
          No accounts match the selected filters.
        </Typography>
      )}
    </Box>
  );
}
